/**
 * Player - Army container that manages soldiers.
 *
 * Dense circle mode:
 * - Deterministic circular packing (Vogel/golden-angle disk).
 * - No periodic breathing/jitter updates.
 * - Formation stays centered on army anchor and only moves via input/count changes.
 */

import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, SAFE_AREA, COLORS } from '../config';
import { ARMY, ARMY_HARD_CAP, EFFECTS } from '../utils/constants';
import { Soldier } from './Soldier';
import { getParticlePool } from '../systems/ParticlePool';

export interface PlayerConfig {
  startingSoldiers?: number;
  trackWidth?: number;
  trackPadding?: number;
}

export class Player extends Phaser.GameObjects.Container {
  private soldiers: Soldier[] = [];

  /** The center X position of the army (for movement) */
  private armyX: number;

  /** Y position of the army (fixed at bottom third) */
  private readonly armyY: number;

  /** Track width used to compute max circular footprint */
  private readonly trackWidth: number;

  /** Track last X for movement dust */
  private lastArmyX: number;

  /** Dust emission timer accumulator */
  private dustTimerMs: number = 0;

  constructor(scene: Phaser.Scene, config: PlayerConfig = {}) {
    const startX = GAME_WIDTH / 2;
    const startY = GAME_HEIGHT - SAFE_AREA.bottom - 120;

    super(scene, startX, startY);

    this.armyX = startX;
    this.armyY = startY;
    this.lastArmyX = startX;
    this.trackWidth = Math.max(1, config.trackWidth ?? GAME_WIDTH - ((config.trackPadding ?? 20) * 2));

    this.addSoldiers(config.startingSoldiers ?? ARMY.startingSoldiers);
    scene.add.existing(this);
  }

  addSoldiers(count: number, animated: boolean = false): void {
    for (let i = 0; i < count; i++) {
      const position = this.calculateFormationPosition(this.soldiers.length, this.soldiers.length + 1);
      const soldier = new Soldier(this.scene, {
        x: this.armyX + position.x,
        y: this.armyY + position.y,
      });

      this.soldiers.push(soldier);
      if (animated) {
        soldier.playSpawnAnimation();
      }
    }

    if (animated && count > 0) {
      this.createMultiplyParticles();
    }

    this.updateFormation();
  }

  private createMultiplyParticles(): void {
    const particleCount = Phaser.Math.Between(8, 12);

    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.graphics();
      particle.fillStyle(COLORS.gate, 0.6);

      const size = Phaser.Math.Between(3, 6);
      particle.fillCircle(0, 0, size);
      particle.setPosition(this.armyX, this.armyY);

      const angle = (i / particleCount) * Math.PI * 2;
      const velocity = Phaser.Math.Between(60, 120);
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;

      this.scene.tweens.add({
        targets: particle,
        x: particle.x + vx * 0.5,
        y: particle.y + vy * 0.5,
        alpha: 0,
        duration: 400,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }
  }

  removeSoldiers(count: number): void {
    const toRemove = Math.min(count, this.soldiers.length);

    for (let i = 0; i < toRemove; i++) {
      const soldier = this.soldiers.pop();
      if (soldier) {
        soldier.playDeathAnimation();
      }
    }

    this.updateFormation();
  }

  getSoldierCount(): number {
    return this.soldiers.length;
  }

  isDefeated(): boolean {
    return this.soldiers.length === 0;
  }

  setArmyX(x: number): void {
    this.armyX = x;
    this.updateFormation();
  }

  getArmyX(): number {
    return this.armyX;
  }

  getArmyY(): number {
    return this.armyY;
  }

  getFormationRadius(): number {
    return this.calculateFormationRadius(this.soldiers.length);
  }

  update(delta: number = 16): void {
    this.spawnMovementDust(delta);
    for (const soldier of this.soldiers) {
      soldier.update();
    }
  }

  /**
   * Circular footprint cap: 90% of track width at ARMY_HARD_CAP.
   */
  private calculateFormationRadius(count: number): number {
    if (count <= 0) return 0;

    const maxRadius = this.trackWidth * 0.45;
    const minRadius = Math.max(10, Soldier.RADIUS * 1.2);
    const normalized = Math.sqrt(Math.min(1, count / ARMY_HARD_CAP));

    return minRadius + (maxRadius - minRadius) * normalized;
  }

  private calculateFormationPosition(index: number, totalCount: number): { x: number; y: number } {
    if (index === 0) {
      return { x: 0, y: 0 };
    }

    const radius = this.calculateFormationRadius(totalCount);
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const angle = index * goldenAngle;
    const normalized = Math.sqrt((index + 0.5) / Math.max(1, totalCount));
    const sampleRadius = radius * normalized;

    return {
      x: Math.cos(angle) * sampleRadius,
      y: Math.sin(angle) * sampleRadius,
    };
  }

  private updateFormation(): void {
    const total = this.soldiers.length;
    for (let i = 0; i < total; i++) {
      const position = this.calculateFormationPosition(i, total);
      this.soldiers[i].setTargetPosition(
        this.armyX + position.x,
        this.armyY + position.y
      );
    }
  }

  private spawnMovementDust(delta: number): void {
    const dx = this.armyX - this.lastArmyX;
    this.lastArmyX = this.armyX;

    const speed = Math.abs(dx) / (delta / 1000);
    if (speed < EFFECTS.dustTrail.minSpeed) {
      this.dustTimerMs = 0;
      return;
    }

    this.dustTimerMs += delta;
    if (this.dustTimerMs < EFFECTS.dustTrail.spawnIntervalMs) {
      return;
    }
    this.dustTimerMs = 0;

    const pool = getParticlePool();
    const count = 2;

    for (let i = 0; i < count; i++) {
      const px = this.armyX + Phaser.Math.Between(-10, 10);
      const py = this.armyY + 14 + Phaser.Math.Between(-4, 4);
      const direction = Math.sign(dx) || 1;
      const vx = -direction * Phaser.Math.Between(EFFECTS.dustTrail.minVelocity, EFFECTS.dustTrail.maxVelocity);
      const vy = Phaser.Math.Between(EFFECTS.dustTrail.minVelocity, EFFECTS.dustTrail.maxVelocity);

      if (pool) {
        pool.spawn({
          x: px,
          y: py,
          shape: 'circle',
          color: COLORS.player,
          alpha: EFFECTS.dustTrail.alpha,
          size: Phaser.Math.Between(EFFECTS.dustTrail.minSize, EFFECTS.dustTrail.maxSize),
          velocityX: vx,
          velocityY: vy,
          duration: Phaser.Math.Between(EFFECTS.dustTrail.minDuration, EFFECTS.dustTrail.maxDuration),
        });
      } else {
        const particle = this.scene.add.graphics();
        particle.fillStyle(COLORS.player, EFFECTS.dustTrail.alpha);
        const size = Phaser.Math.Between(EFFECTS.dustTrail.minSize, EFFECTS.dustTrail.maxSize);
        particle.fillCircle(0, 0, size);
        particle.setPosition(px, py);

        this.scene.tweens.add({
          targets: particle,
          x: particle.x + vx * 0.2,
          y: particle.y + vy * 0.2,
          alpha: 0,
          duration: Phaser.Math.Between(EFFECTS.dustTrail.minDuration, EFFECTS.dustTrail.maxDuration),
          ease: 'Cubic.easeOut',
          onComplete: () => particle.destroy(),
        });
      }
    }
  }

  getSoldiers(): Soldier[] {
    return this.soldiers;
  }

  applyMultiplier(multiplier: number): number {
    const currentCount = this.soldiers.length;
    const newCount = Math.floor(currentCount * multiplier);
    const soldiersToAdd = newCount - currentCount;

    if (soldiersToAdd > 0) {
      this.addSoldiers(soldiersToAdd, true);
    }

    return soldiersToAdd;
  }

  destroy(fromScene?: boolean): void {
    for (const soldier of this.soldiers) {
      soldier.destroy();
    }
    this.soldiers = [];

    super.destroy(fromScene);
  }
}
